import { useResizeObserver } from '@byecode/ui'
import type { RelativeSelectConfig } from '@lighthouse/core'
import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import type { FieldBaseProps } from '../../types'
import { RelationCheckboxPreview } from './RelationCheckboxPreview'
import { RecordRecordSelect } from './RelationRecordSelect'
import { RelationTagPreview } from './RelationTagPreview'
import { useRelativeDataController } from './RelativeDataController'

interface RelativeSelectFieldProps extends FieldBaseProps {
    value?: string
    relativeSelectConfig: RelativeSelectConfig
}

const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
    border-radius: inherit;
`

const RelativeSelectField: React.FunctionComponent<RelativeSelectFieldProps> = props => {
    const {
        id,
        onChange,
        value,
        readOnly,
        config,
        relativeSelectConfig,
        dataSourceList,
        isControlled,
        onFetchDataSource,
        onLoadMoreData,
        onAddClickEvent,
        onRenderLabel
    } = props
    const { breakPoint } = config
    const { relativeSelect } = relativeSelectConfig
    const {
        relativePointer = '',
        showType = 'table',
        showMode,
        direction,
        align,
        canMultipleChoice,
        relativeShowFieldPointer = '',
        relativeFieldPointer = ''
    } = relativeSelect ?? {}

    const {
        relativeDataSource: newRelativeDataSource,
        recordValue,
        recordPoints,
        relativeField,
        relativeShowField,
        onRecordChange,
        onChange: onChangeValue
    } = useRelativeDataController({ id, onChange, value, isControlled, config, relativeSelectConfig, dataSourceList })

    // 获取数据源的 schema
    const schema = useMemo(() => newRelativeDataSource?.schema, [newRelativeDataSource])

    const [containerRef, containerRect] = useResizeObserver<HTMLDivElement>()
    const dropDownRef = useRef<HTMLDivElement>(null)
    const [opened, setOpened] = useState(false)

    // 获取字段信息
    const field = useMemo(() => schema?.[relativeFieldPointer], [schema, relativeFieldPointer])
    const showField = useMemo(
        () => schema?.[relativeShowFieldPointer] || schema?.[relativeFieldPointer],
        [schema, relativeShowFieldPointer, relativeFieldPointer]
    )

    const fieldInfo = useMemo(() => {
        if (!field || !showField || !newRelativeDataSource) {
            return null
        }
        return {
            field,
            showField,
            dsId: newRelativeDataSource
        }
    }, [field, showField, newRelativeDataSource])

    return useMemo(() => {
        switch (showMode) {
            case 'checkbox': {
                return (
                    <SCxContainer>
                        <RelationCheckboxPreview
                            breakPoint={breakPoint}
                            recordPoints={recordPoints}
                            dsId={relativePointer}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={recordValue}
                            disabled={readOnly}
                            onChange={onChangeValue}
                            onRenderLabel={onRenderLabel}
                        />
                    </SCxContainer>
                )
            }
            case 'tag': {
                if (!fieldInfo) {
                    return null
                }
                return (
                    <SCxContainer>
                        <RelationTagPreview
                            dsId={relativePointer}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            recordPoints={recordPoints}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={recordValue}
                            disabled={readOnly}
                            onChange={onChangeValue}
                        />
                    </SCxContainer>
                )
            }
            default: {
                return (
                    <SCxContainer ref={containerRef} data-field-input data-field-active={opened} data-field-disabled={!!readOnly}>
                        <RecordRecordSelect
                            id={id}
                            breakPoint={breakPoint}
                            dsId={relativePointer}
                            dataSource={newRelativeDataSource}
                            readOnly={readOnly}
                            opened={opened}
                            containerRect={containerRect}
                            containerRef={containerRef}
                            relativeSelectConfig={relativeSelectConfig}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            showType={showType}
                            ref={dropDownRef}
                            dataSourceList={dataSourceList || []}
                            value={recordValue}
                            onChange={onRecordChange}
                            onAddClickEvent={onAddClickEvent}
                            onFetchDataSource={onFetchDataSource}
                            onLoadMoreData={onLoadMoreData}
                            onChangeOpened={setOpened}
                        />
                    </SCxContainer>
                )
            }
        }
    }, [
        showMode,
        breakPoint,
        recordPoints,
        relativePointer,
        relativeField,
        relativeShowField,
        canMultipleChoice,
        direction,
        align,
        recordValue,
        readOnly,
        onChangeValue,
        onRenderLabel,
        fieldInfo,
        containerRef,
        opened,
        id,
        newRelativeDataSource,
        containerRect,
        relativeSelectConfig,
        showType,
        dataSourceList,
        onRecordChange,
        onAddClickEvent,
        onFetchDataSource,
        onLoadMoreData
    ])
}

export default RelativeSelectField
